import { OrderLineCOA } from '@ravago/shared/page-data/models/elements';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { IconButton } from '@ravago/shared/radiance/components/button/components/IconButton/IconButton';
import { Divider } from '@ravago/shared/radiance/components/divider/components/Divider/Divider';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

interface Props {
   documents: Array<OrderLineCOA>;
   onDownload: (document: OrderLineCOA) => void;
   onRequestCoa: (coa: OrderLineCOA) => void;
   onViewDocument: (document: OrderLineCOA) => void;
}

export const CoaDocuments = ({ documents, onDownload, onRequestCoa, onViewDocument }: Props) => {
   const translate = useTranslations();
   const format = useFormatter();

   if (documents.length === 0) {
      return (
         <Typography variant="body" component="span">
            {translate('page-consumer.order-line-flyout.no-documents-available')}
         </Typography>
      );
   }

   return documents.map((document, index) => (
      // eslint-disable-next-line @eslint-react/no-array-index-key
      <Box key={index} gap="none">
         <Box direction="column" gap="sm" width="100%">
            <Box gap="xl">
               <Box direction="column" gap="none">
                  <Typography variant="body" component="span" color="subtle">
                     {translate('page-consumer.order-line-flyout.transport-order-number')}
                  </Typography>
                  <Typography variant="body" component="span">
                     {document.transportOrderNr}
                  </Typography>
               </Box>
               <Box direction="column" gap="none">
                  <Typography variant="body" component="span" color="subtle">
                     {translate('page-consumer.order-line-flyout.producer-lot-number')}
                  </Typography>
                  <Typography variant="body" component="span">
                     {document.producerLotNr}
                  </Typography>
               </Box>
            </Box>
            <Box justify="between">
               <Box direction="column" gap="none">
                  <Typography variant="body" component="span" color="subtle">
                     {translate('page-consumer.order-line-flyout.quantity')}
                  </Typography>
                  <Typography variant="body" component="span">
                     {format.quantity(document.batch.quantity)}
                  </Typography>
               </Box>
               <Box>
                  {document.isAvailable ? (
                     <Box gap="2xs">
                        <IconButton icon="download" ariaLabel="Download COA" onClick={() => onDownload(document)} />
                        <IconButton icon="eye" ariaLabel="View COA" onClick={() => onViewDocument(document)} />
                     </Box>
                  ) : (
                     <Button variant="tertiary" onClick={() => onRequestCoa(document)}>
                        {translate('page-consumer.order-line-flyout.request')}
                     </Button>
                  )}
               </Box>
            </Box>
            {index < documents.length - 1 && <Divider />}
         </Box>
      </Box>
   ));
};
